import React, { useMemo, useState, useEffect, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Home.css";
import { useDispatch } from 'react-redux';
import { addItem, delItem } from '../redux/action/index';
import { sendRequest } from "../ApiRequest";
import { sendCachedRequest } from "../utils/apiCache";
import { BASE_URL } from "../URL/constants";
import getCurrentUser from "../components/common/GetCurrentUser";
import { useLocation, useNavigate } from "react-router-dom";
import useHtmlLinkNavigation from "../hooks/useHtmlLinkNavigation";
import Login from "./buttons/Login";
import SignupModal from "./buttons/SignupModal";
import AddToCartButton from "../components/common/AddToCartButton";
import { isItemOutOfStock } from '../utils/stockUtils';
import CheckoutHandler from "./common/CheckoutHandler";
import { addGuestCartItem, removeGuestCartItem } from "../utils/guestCart";
import SalonHome from "./SalonHome";
import RetailHome from "./RetailHome";
import GroceriesHome from "./GroceriesHome";
import CosmeticHome from "./CosmeticHome";
import GeneralBusinessHome from "./GeneralBusinessHome";
import PetHome from "./PetHome";

const SALON_KEYWORDS = ["salon", "saloon", "spa", "wellness", "beauty", "service"];

// const formatPrice = (value = 0) => `₹${Number(value || 0).toFixed(2)}`;

// const getDiscountedPrice = (product) =>
//   product.sales_discount > 0
//     ? Math.max(product.sales_price - product.sales_discount, 0)
//     : product.sales_price;

// const getDiscountPercent = (product) => {
//   if (!product.sales_discount || !product.sales_price) return 0;
//   return Math.round((product.sales_discount / product.sales_price) * 100);
// };

const formatPrice = (value = 0) => `₹${Number(value || 0).toFixed(2)}`;

const getDiscountedPrice = (product) =>
  product.sales_discount > 0
    ? Math.max(product.sales_price - product.sales_discount, 0)
    : product.sales_price;

const getDiscountPercent = (product) => {
  if (!product.sales_discount || !product.sales_price) return 0;
  return Math.round((product.sales_discount / product.sales_price) * 100);
};

const Home = () => {
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("ALL PRODUCTS");
  const [categories, setCategories] = useState([]);
  const [navLoading, setNavLoading] = useState(true);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const { isLoggedIn, currentUser } = getCurrentUser();
  const [carouselHtml, setCarouselHtml] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [tagline, setTagline] = useState("");

  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [pendingCheckoutItem, setPendingCheckoutItem] = useState(null);
  
  // SignupModal states
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showOtpLogin, setShowOtpLogin] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [businessType, setBusinessType] = useState(null);
  const [businessCategory, setBusinessCategory] = useState(null);
  const [businessInfoReady, setBusinessInfoReady] = useState(false);
  const [companyName, setCompanyName] = useState("Looks Salon");
  const [bodyHtml, setBodyHtml] = useState([]);
  const bodyContentRef = useHtmlLinkNavigation(bodyHtml);
  const [branches, setBranches] = useState([]);
  const customerData = currentUser?.data?.customer ?? null;
  // Check if it's a pet products business (should use PetHome, not SalonHome)
  const isPetProductsBusiness = useMemo(() => {
    const category = (businessCategory || "").toLowerCase();
    return category === "pet_products";
  }, [businessCategory]);

  const isSalonBusiness = useMemo(() => {
    // Don't treat pet_products as salon business
    if (isPetProductsBusiness) return false;
    const type = (businessType || "").toLowerCase();
    const category = (businessCategory || "").toLowerCase();
    return SALON_KEYWORDS.some((keyword) => type.includes(keyword) || category.includes(keyword));
  }, [businessType, businessCategory, isPetProductsBusiness]);

  const isRetailBusiness = useMemo(() => {
    const type = (businessType || "").toLowerCase();
    const category = (businessCategory || "").toLowerCase();
    const retailCategories = ["groceries", "cosmetic", "general_business"];
    return type === "retail" && retailCategories.includes(category);
  }, [businessType, businessCategory]);

  const retailCategory = useMemo(() => {
    if (!isRetailBusiness) return null;
    const category = (businessCategory || "").toLowerCase();
    if (category === "groceries") return "groceries";
    if (category === "cosmetic") return "cosmetic";
    if (category === "general_business") return "general_business";
    return null;
  }, [isRetailBusiness, businessCategory]);

  const visibleCategories = showAllCategories ? categories : categories.slice(0, 4);


  // Carousel settings
  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: true,
    fade: true,
  };
  
  // Add default image fallback
  const defaultImg = "https://images.unsplash.com/photo-1592078615290-033ee584e267?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&h=500&q=80";

  useEffect(() => {
    const fetchCarouselContent = async () => {
      try {
        const response = await sendCachedRequest(
          "POST",
          `${BASE_URL}/v1/public_site/public_site_info/get_list`,
          null,
          { cacheKey: "public_site_info" }
        );
      
        const content = response.data.data.public_site_info?.carousel_image_urls;
        const bodyContent = response.data.data.public_site_info?.body_content.body;
        const tag_line = response?.data?.data?.public_site_info?.tagline_section;
        const apiCompanyName =
          response?.data?.data?.company_info?.company_name ||
          response?.data?.data?.business_name ||
          response?.data?.data?.company_info?.name;
        const apiBusinessType = response?.data?.data?.business_type;
        const apiBusinessCategory = response?.data?.data?.business_category;
        
        // Extract branches from company_info
        const allBranches = response?.data?.data?.company_info?.all_branches || [];
        if (allBranches.length > 0) {
          // Map branches to match the expected format
          const formattedBranches = allBranches.map((branch) => ({
            id: branch.id,
            name: branch.branch_name,
            city: branch.city,
            address: branch.address,
            state: branch.state,
            pin_code: branch.pin_code
          }));
          setBranches(formattedBranches);
        }
        if (apiCompanyName) {
          setCompanyName(apiCompanyName);
        }

        setBusinessType(apiBusinessType);
        setBusinessCategory(apiBusinessCategory);

        if (content || bodyContent) {
          setCarouselHtml(content);
          setBodyHtml(bodyContent);
          setTagline(tag_line);
        }
      } catch (error) {
        console.error("Error fetching carousel content:", error);
      } finally {
        setBusinessInfoReady(true);
      }
    };

    fetchCarouselContent();
  }, [currentUser]);


  // Effect to control SignupModal
  useEffect(() => {
    if (signupModalOpen) {
      const modal = document.getElementById('signupModal');
      if (modal && typeof window !== 'undefined' && window.bootstrap) {
        const bootstrapModal = new window.bootstrap.Modal(modal);
        bootstrapModal.show();
        
        // Listen for modal close event
        const handleModalHidden = () => {
          setSignupModalOpen(false);
          setPendingCheckoutItem(null);
          setShowOtpLogin(false);
          setPhoneNumber("");
          setOtp("");
          setFirstName("");
          setLastName("");
          setEmail("");
        };
        
        modal.addEventListener('hidden.bs.modal', handleModalHidden);
        
        // Cleanup function
        return () => {
          modal.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
      }
    }
  }, [signupModalOpen]);

  const fetchCategories = useCallback(async () => {
    try {
      setNavLoading(true);
      const response = await sendCachedRequest(
        "GET",
        `${BASE_URL}/v1/public_site/menus/fetch_categories`,
        null,
        { cacheKey: "menu_categories" }
      );
      
      const allProductsCategory = { 
        id: "all", 
        name: "ALL PRODUCTS" 
      };

      setCategories([allProductsCategory, ...response.data.categories]);
      setActiveCategory(allProductsCategory);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      setError("Failed to load navigation. Please try again later.");
    } finally {
      setNavLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!businessInfoReady) {
      return;
    }
    if (!isSalonBusiness) {
      fetchCategories();
    } else {
      setCategories([]);
      setNavLoading(false);
    }
  }, [businessInfoReady, isSalonBusiness, fetchCategories]);

  const fetchAllProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sendCachedRequest(
        "GET",
        `${BASE_URL}/v1/public_site/menus/item_lists?per_page=1000`,
        null,
        { cacheKey: "item_lists_all" }
      );

      // Transform API data to match our structure
      const products = response.data.items.map((item) => ({
        id: item.id,
        item_name: item.item_name,
        sales_price: item.sales_price,
        sales_discount: item.sales_discount || 0,
        quantity: item.quantity,
        image: item.item_images?.[0] || defaultImg,
        category: item.category || "ALL PRODUCTS",
      }));

      setAllProducts(products);
      setFeaturedProducts(products);
    } catch (err) {
      console.error("Failed to fetch products:", err);
      setError("Failed to load products. Please try again later.");
      setAllProducts([]);
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  }, [defaultImg]);


  const fetchProductsByCategory = async (categoryId) => {
    try {
      setLoading(true);
      setError(null);

      if (categoryId === "all") {
        setFeaturedProducts(allProducts);
        setLoading(false);
        return;
      }
      const response = await sendCachedRequest(
        "GET",
        `${BASE_URL}/v1/public_site/menus/item_lists?per_page=1000`,
        null,
        {
          params: { category_id: categoryId, per_page: 1000 },
          cacheKey: `item_lists_category_${categoryId}`,
        }
      );

      // Transform API data to match our structure
      const products = response.data.items.map((item) => ({
        id: item.id,
        item_name: item.item_name,
        sales_price: item.sales_price,
        sales_discount: item.sales_discount || 0,
        quantity: item.quantity,
        image: item.item_images?.[0] || defaultImg,
        category: item.category || "",
      }));

      setFeaturedProducts(products);
    } catch (err) {
      console.error(`Failed to fetch products for category ${categoryId}:`, err);
      setError("Failed to load products. Please try again later.");
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    if (!isSalonBusiness) {
      fetchProductsByCategory(category.id);
    }
  };

  useEffect(() => {
    if (!businessInfoReady) {
      return;
    }
    if (!isSalonBusiness) {
      fetchAllProducts();
    } else {
      setAllProducts([]);
      setFeaturedProducts([]);
      setLoading(false);
    }
  }, [businessInfoReady, isSalonBusiness, fetchAllProducts]);

  // Force slick slider to reinitialize on mobile
  useEffect(() => {
    if (featuredProducts.length > 0 && !isSalonBusiness) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        // Trigger window resize to force slick to recalculate
        window.dispatchEvent(new Event('resize'));
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [featuredProducts.length, activeCategory, isSalonBusiness]);

  const handleCart = async (product) => {
    const currentUser = JSON.parse(localStorage.getItem("userDetails"));
    let cartState = JSON.parse(localStorage.getItem("cartState")) || {};
    const isInCart = cartState[product.id];
    let guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
    console.log('Current user:', currentUser);
    console.log('Cart state:', cartState);
    console.log('Is in cart:', isInCart);

    if (!currentUser || !currentUser.data || !currentUser.data.token || !currentUser.data.customer) {
      if (!isInCart) {
        const updatedCart = [...guestCart, { ...product, quantity }];
        localStorage.setItem("guestCart", JSON.stringify(updatedCart));
        dispatch(addItem({ ...product, quantity }));
        cartState[product.id] = true;
      } else {
        const guestCartArray = Object.values(guestCart);
        const updatedCart = guestCartArray.filter(item => item.id !== product.id);
        localStorage.setItem("guestCart", JSON.stringify(updatedCart));
        dispatch(delItem(product));
        delete cartState[product.id];
      }
      localStorage.setItem("cartState", JSON.stringify(cartState));
      // Dispatch cart-updated event to update cart count immediately
      window.dispatchEvent(new Event('cart-updated'));
      return;
    }

    const token = currentUser.data.token;
    const customerId = currentUser.data.customer.id;

    // Always add to cart when checkout is clicked (don't check cartBtn state)
    const cartData = {
      customer_id: customerId,
      item_id: product.id,
      quantity: quantity || 1
    };

    try {
      const response = await sendRequest(
        'POST',
        `${BASE_URL}/v1/public_site/customers/${customerId}/cart/cart_items`,
        cartData,
        { Authorization: token }
      );

      if (response.data) {
        // Add to Redux state with quantity
        const itemToAdd = { ...product, quantity: quantity || 1 };
        dispatch(addItem(itemToAdd));
        cartState[product.id] = true;
        localStorage.setItem("cartState", JSON.stringify(cartState));
        // Dispatch cart-updated event to update cart count immediately
        window.dispatchEvent(new Event('cart-updated'));
      } else {
        console.error("Error adding to cart:", response.message);
      }
    } catch (err) {
      console.error("API error:", err);
      // Even if API fails, add to Redux state for immediate use
      dispatch(addItem({ ...product, quantity: quantity || 1 }));
      cartState[product.id] = true;
      localStorage.setItem("cartState", JSON.stringify(cartState));
      window.dispatchEvent(new Event('cart-updated'));
    }
  };

  const handleCheckout = async (item) => {
    // Use common checkout handler with the specific item
    const { handleCheckout } = CheckoutHandler({
      items: [item],
      onLoginRequired: () => {
        setPendingCheckoutItem(item);
        setSignupModalOpen(true);
      },
      navigate,
      dispatch,
      isLoggedIn
    });
    await handleCheckout();
  };
  
  const closeLoginModal = () => {
    setLoginModalOpen(false);
    setPendingCheckoutItem(null);
  };
  
  const handleLoginSuccess = () => {
    closeLoginModal();
    // If there's a pending checkout item, add it to cart and navigate
    if (pendingCheckoutItem) {
      handleCart(pendingCheckoutItem).then(() => {
        navigate('/customer-info');
      });
      setPendingCheckoutItem(null);
    } else {
      // Navigate to customer info form after successful login
      navigate('/customer-info');
    }
  };

  // SignupModal handlers
  const closeSignupModal = () => {
    setSignupModalOpen(false);
    setPendingCheckoutItem(null);
    setShowOtpLogin(false);
    setPhoneNumber("");
    setOtp("");
    setFirstName("");
    setLastName("");
    setEmail("");
    // Close Bootstrap modal
    const modal = document.getElementById('signupModal');
    if (modal && typeof window !== 'undefined' && window.bootstrap) {
      const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
      }
    }
  };

  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      console.log("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/send_otp`,
        { phone: phoneNumber }
      );

      if (response.data) {
        setShowOtpLogin(true);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      console.log("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/verify_otp`,
        { phone: phoneNumber, otp: otp }
      );

      if (response.data) {
        // Check if user exists
        if (response.data.user_exists) {
          // User exists, log them in
          localStorage.setItem("userDetails", JSON.stringify(response.data));
          
          // Close modal properly
          const modalElement = document.getElementById("signupModal");
          if (modalElement && window.bootstrap) {
            const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
            if (modalInstance) {
              modalInstance.hide();
            }
          }
          closeSignupModal();
          
          // If there's a pending checkout item, add it to cart and navigate
          setTimeout(() => {
            if (pendingCheckoutItem) {
              handleCart(pendingCheckoutItem).then(() => {
                navigate('/customer-info');
              });
              setPendingCheckoutItem(null);
            } else {
              navigate('/customer-info');
            }
          }, 300);
        } else {
          // New user, show signup fields
          setActiveTab("signup");
        }
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    
    if (!firstName || !lastName || !email) {
      console.log("Please fill in all required fields");
      return;
    }

    try {
      const response = await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/signup`,
        {
          phone: phoneNumber,
          first_name: firstName,
          last_name: lastName,
          email: email,
          otp: otp
        }
      );

      if (response.data) {
        localStorage.setItem("userDetails", JSON.stringify(response.data));
        
        // Close modal properly
        const modalElement = document.getElementById("signupModal");
        if (modalElement && window.bootstrap) {
          const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
          if (modalInstance) {
            modalInstance.hide();
          }
        }
        closeSignupModal();
        
        // If there's a pending checkout item, add it to cart and navigate
        setTimeout(() => {
          if (pendingCheckoutItem) {
            handleCart(pendingCheckoutItem).then(() => {
              navigate('/customer-info');
            });
            setPendingCheckoutItem(null);
          } else {
            navigate('/customer-info');
          }
        }, 300);
      }
    } catch (error) {
      console.error("Error signing up:", error);
    }
  };

  if (!businessInfoReady) {
    return null;
  }
  
  // Check for pet products business first
  if (isPetProductsBusiness) {
    return (
      <PetHome
        carouselHtml={carouselHtml}
        tagline={tagline}
        companyName={companyName}
        bodyHtml={bodyHtml}
        branches={branches}
      />
    );
  }
  
  if (isRetailBusiness) {
    // Render specific component based on category
    if (retailCategory === "groceries") {
      return (
        <GroceriesHome
          carouselHtml={carouselHtml}
          tagline={tagline}
          companyName={companyName}
          bodyHtml={bodyHtml}
        />
      );
    }
    
    if (retailCategory === "cosmetic") {
      return (
        <CosmeticHome
          carouselHtml={carouselHtml}
          tagline={tagline}
          companyName={companyName}
          bodyHtml={bodyHtml}
        />
      );
    }
    
    if (retailCategory === "general_business") {
      return (
        <GeneralBusinessHome
          carouselHtml={carouselHtml}
          tagline={tagline}
          companyName={companyName}
          bodyHtml={bodyHtml}
        />
      );
    }
    
    // Fallback to RetailHome if category doesn't match
    return (
      <RetailHome
        carouselHtml={carouselHtml}
        tagline={tagline}
        companyName={companyName}
        bodyHtml={bodyHtml}
      />
    );
  }

  if (isSalonBusiness) {
    return (
      <SalonHome
        carouselHtml={carouselHtml}
        tagline={tagline}
        companyName={companyName}
        bodyHtml={bodyHtml}
        branches={branches}
      />
    );
  }

  return (
    <div className="home-page">
      <div className="carousel-wrapper">
        <Slider {...settings}>
          {carouselHtml.map((image, index) => (
            <div key={index} className="carousel-slide">
              <img
                src={image}
                alt={`Slide ${index + 1}`}
                className="carousel-image"
              />
            </div>
          ))}
        </Slider>
      </div>

      {/* Text Sections */}
      <div dangerouslySetInnerHTML={{ __html: tagline }} />

      {/* Featured Products Section */}
      <div className="featured-products">
        <div className="container">
          <h2 className="section-title">Featured Products</h2>

          {/* Categories Filter */}
          <div className="categories-filter">
            {navLoading ? (
              <div className="loading-categories">Loading categories...</div>
            ) : (
              <>
                {visibleCategories.map((category) => (
                  <button
                    key={category?.id}
                    className={`category-btn ${
                      activeCategory?.id === category?.id ? "active" : ""
                    }`}
                    onClick={() => handleCategoryChange(category)}
                  >
                    {category?.name}
                  </button>
                ))}

                {categories?.length > 4 && (
                  <button
                    className="category-btn toggle-btn"
                    onClick={() => setShowAllCategories(!showAllCategories)}
                  >
                    {showAllCategories ? "Show Less" : "More"}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading products...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={() => activeCategory?.id === "all" ? fetchAllProducts() : fetchProductsByCategory(activeCategory?.id)}>
                Retry
              </button>
            </div>
          )}

          {/* Products Slider */}
          {!loading && !error && (
            <div className="products-slider-container">
              {featuredProducts.length > 0 ? (
                <Slider
                  key={`slider-${featuredProducts.length}-${activeCategory?.id}`}
                  dots={true}
                  infinite={featuredProducts.length > 4}
                  speed={500}
                  slidesToShow={4}
                  slidesToScroll={4}
                  arrows={true}
                  responsive={[
                    {
                      breakpoint: 1024,
                      settings: {
                        slidesToShow: 3,
                        slidesToScroll: 3,
                        arrows: true,
                      },
                    },
                    {
                      breakpoint: 768,
                      settings: {
                        slidesToShow: 2,
                        slidesToScroll: 2,
                        arrows: true,
                      },
                    },
                    {
                      breakpoint: 480,
                      settings: {
                        slidesToShow: 1,
                        slidesToScroll: 1,
                        arrows: true,
                        dots: false,
                        swipe: true,
                        swipeToSlide: true,
                        touchMove: true,
                        touchThreshold: 5,
                        variableWidth: false,
                        centerMode: false,
                        adaptiveHeight: false,
                        lazyLoad: 'ondemand',
                      },
                    },
                  ]}
                >
                  {featuredProducts.map((product) => {
                    const discountPercent = getDiscountPercent(product);
                    const discountedPrice = getDiscountedPrice(product);
                    const outOfStock = isItemOutOfStock(product, quantity);
                    const lowInventory =
                      !outOfStock &&
                      typeof product.quantity === "number" &&
                      product.quantity > 0 &&
                      product.quantity <= 5;
                    const stockLabel = outOfStock
                      ? "Waitlist"
                      : lowInventory
                        ? "Few left"
                        : "Ready to ship";
                    const stockClass = outOfStock
                      ? "sold-out"
                      : lowInventory
                        ? "low"
                        : "fresh";

                    return (
                      <div key={product.id} className="product-slide">
                        <article
                          className={`product-card capsule-card ${
                            hoveredProduct === product.id ? "is-hovered" : ""
                          }`}
                          onMouseEnter={() => setHoveredProduct(product.id)}
                          onMouseLeave={() => setHoveredProduct(null)}
                        >
                          <div
                            className="capsule-media"
                            onClick={() => navigate(`/products/${product.id}`)}
                            role="button"
                            tabIndex={0}
                          >
                            {discountPercent > 0 && (
                              <span className="capsule-pill">
                                Save {discountPercent}%
                              </span>
                            )}
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.item_name}
                                onLoad={(e) => {
                                  e.target.style.display = "block";
                                  e.target.parentElement.style.backgroundImage = "none";
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.parentElement.style.backgroundImage =
                                    'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'100\' height=\'100\'%3E%3Crect fill=\'%23f5f5f5\' width=\'100\' height=\'100\'/%3E%3Ctext fill=\'%23999\' font-family=\'sans-serif\' font-size=\'14\' dy=\'10.5\' x=\'50%25\' y=\'50%25\' text-anchor=\'middle\'%3ENo Image%3C/text%3E%3C/svg%3E")';
                                }}
                              />
                            ) : null}
                          </div>

                          <div className="capsule-body">
                            <div className="capsule-top">
                              <div>
                                {product.category && (
                                  <p className="product-category-label">
                                    {product.category}
                                  </p>
                                )}
                                <h3 className="capsule-title">{product.item_name}</h3>
                              </div>
                              <span className={`stock-indicator ${stockClass}`}>
                                {stockLabel}
                              </span>
                            </div>

                            <div className="capsule-price">
                              <span className="price-current">
                                {formatPrice(discountedPrice)}
                              </span>
                              {product.sales_discount > 0 && (
                                <>
                                  <span className="price-original">
                                    {formatPrice(product.sales_price)}
                                  </span>
                                  <span className="price-save">
                                    Save {formatPrice(product.sales_discount)}
                                  </span>
                                </>
                              )}
                            </div>

                            <div className="capsule-actions">
                              <AddToCartButton
                                product={product}
                                quantity={quantity}
                                setQuantity={setQuantity}
                              />
                            </div>
                          </div>
                        </article>
                      </div>
                    );
                  })}
                </Slider>
              ) : (
                <div className="no-products">
                  <p>No products found in this category.</p>
                </div>
              )}
            </div>
          )}

          <blockquote>
            <div ref={bodyContentRef} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
          </blockquote>
        </div>
      </div>
      
      <Login 
        isOpen={loginModalOpen}
        onClose={closeLoginModal}
        onLoginSuccess={handleLoginSuccess}
        customerData={customerData}
      />
      
      <SignupModal
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        showOtpLogin={showOtpLogin}
        setShowOtpLogin={setShowOtpLogin}
        handleSignUp={handleSignUp}
        handleSendOtp={handleSendOtp}
        handleVerifyOtp={handleVerifyOtp}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        otp={otp}
        setOtp={setOtp}
        firstName={firstName}
        setFirstName={setFirstName}
        lastName={lastName}
        setLastName={setLastName}
        email={email}
        setEmail={setEmail}
      />
    </div>
  );
};

export default Home;